import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DollarOutlined,
  QrcodeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { eventRegistrationService, eventService } from '../../services';
import apiClient from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title, Text } = Typography;

const formatMoney = (value) => new Intl.NumberFormat('vi-VN', {
  style: 'currency',
  currency: 'VND',
  maximumFractionDigits: 0
}).format(Number(value) || 0);

const paymentStatusTag = (status) => {
  if (!status) return <Tag>Legacy</Tag>;
  if (status === 'Đã Thanh Toán') return <Tag color="green">{status}</Tag>;
  if (status === 'Không Cần Thanh Toán') return <Tag color="blue">{status}</Tag>;
  if (status === 'Hủy') return <Tag color="red">{status}</Tag>;
  return <Tag color="gold">{status}</Tag>;
};

const approvalStatusTag = (status) => {
  if (status === 'Đã Duyệt') return <Tag color="green">Đã Duyệt</Tag>;
  if (status === 'Bị Từ Chối') return <Tag color="red">Bị Từ Chối</Tag>;
  return <Tag color="gold">{status || 'Chưa Duyệt'}</Tag>;
};

const detailRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  lineHeight: 1.7
};

const detailLabelStyle = {
  color: '#8c8c8c',
  whiteSpace: 'nowrap'
};

const EventRegistrations = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = Number(id);

  const [event, setEvent] = useState(location.state?.event || null);
  const [eventLoading, setEventLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAttendees, setTotalAttendees] = useState(0);
  const [totalDinner, setTotalDinner] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchText, setSearchText] = useState('');

  const [cashModalVisible, setCashModalVisible] = useState(false);
  const [cashRecord, setCashRecord] = useState(null);
  const [cashAmount, setCashAmount] = useState(0);
  const [cashNote, setCashNote] = useState('');
  const [cashSubmitting, setCashSubmitting] = useState(false);

  const loadEvent = async () => {
    if (!Number.isInteger(eventId) || eventId <= 0) return;
    setEventLoading(true);
    try {
      const response = await eventService.getAll({ id: eventId, page: 1, pageSize: 1 });
      const list = Array.isArray(response) ? response : [];
      const found = list.find(item => Number(item.IdSuKien) === eventId) || list[0] || null;
      if (found) setEvent(found);
    } catch (error) {
      if (!event) {
        message.error(error.response?.data?.error || 'Không thể tải thông tin sự kiện');
      }
    } finally {
      setEventLoading(false);
    }
  };

  const loadRegistrations = async (nextPage = pagination.current, nextPageSize = pagination.pageSize, nextStatus = statusFilter) => {
    if (!Number.isInteger(eventId) || eventId <= 0) return;
    setLoading(true);
    try {
      const response = await eventRegistrationService.getRegistrations({
        idsukien: eventId,
        trangthai: nextStatus || undefined,
        page: nextPage,
        pageSize: nextPageSize
      });

      const data = Array.isArray(response)
        ? response
        : (Array.isArray(response?.data) ? response.data : []);

      setRegistrations(data);
      setTotalAttendees(Number(response?.totalAttendees) || 0);
      setTotalDinner(Number(response?.totalDinner) || 0);
      setTotalPaidAmount(Number(response?.totalPaidAmount) || 0);
      setPagination(prev => ({
        ...prev,
        current: nextPage,
        pageSize: nextPageSize,
        total: Number(response?.totalRecords) || data.length
      }));
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || 'Lỗi khi tải danh sách đăng ký');
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Number.isInteger(eventId) || eventId <= 0) {
      message.error('ID sự kiện không hợp lệ');
      navigate('/events/list', { replace: true });
      return;
    }
    loadEvent();
    loadRegistrations(1, pagination.pageSize, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const filteredRegistrations = useMemo(() => {
    const keyword = searchText.trim().toLocaleLowerCase('vi-VN');
    if (!keyword) return registrations;
    return registrations.filter(record => [
      record.TenNguoiDangKy,
      record.SDTNguoiDangKy,
      record.Gmail,
      record.TenDoanhNghiep,
      record.MaThanhToan
    ].some(value => String(value || '').toLocaleLowerCase('vi-VN').includes(keyword)));
  }, [registrations, searchText]);

  const canApproveRegistration = (record) => {
    const paymentStatus = record.TrangThaiThanhToan;
    if (paymentStatus === null || paymentStatus === undefined || paymentStatus === '') return true;
    if (paymentStatus === 'Không Cần Thanh Toán') return true;
    if (paymentStatus !== 'Đã Thanh Toán') return false;
    return Number(record.SoTienDaThanhToan || 0) >= Number(record.SoTienPhaiThu || 0);
  };

  const handleRegistrationStatusChange = async (record, newStatus) => {
    if (newStatus === record.TrangThai) return;

    if (newStatus === 'Đã Duyệt' && !canApproveRegistration(record)) {
      message.warning('Đăng ký chưa hoàn tất thanh toán nên chưa thể duyệt.');
      return;
    }

    try {
      await eventRegistrationService.updateStatus(record.id, newStatus);
      message.success('Cập nhật trạng thái đăng ký thành công');
      await loadRegistrations();
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || 'Lỗi khi cập nhật trạng thái');
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    try {
      await eventRegistrationService.delete(registrationId);
      message.success('Xóa đăng ký thành công');
      await loadRegistrations();
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || 'Không thể xóa đăng ký');
    }
  };

  const openCashModal = (record) => {
    const due = Number(record.SoTienPhaiThu || 0);
    setCashRecord(record);
    setCashAmount(due);
    setCashNote('');
    setCashModalVisible(true);
  };

  const confirmCashPayment = async () => {
    if (!cashRecord) return;
    const expected = Number(cashRecord.SoTienPhaiThu || 0);

    if (!Number.isFinite(cashAmount) || cashAmount <= 0) {
      message.error('Số tiền nhận phải lớn hơn 0');
      return;
    }
    if (cashAmount !== expected) {
      message.error(`Tiền mặt phải đúng số tiền phải thu: ${formatMoney(expected)}`);
      return;
    }

    setCashSubmitting(true);
    try {
      const response = await apiClient.post(
        `/api/dangkysukien/manager/${cashRecord.id}/confirm-cash-payment`,
        { soTienNhan: cashAmount, ghiChu: cashNote.trim() || null }
      );
      message.success(response.data?.message || 'Xác nhận tiền mặt thành công');
      setCashModalVisible(false);
      setCashRecord(null);
      await loadRegistrations();
    } catch (error) {
      message.error(error.response?.data?.message || error.response?.data?.error || 'Không thể xác nhận tiền mặt');
    } finally {
      setCashSubmitting(false);
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value || null);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadRegistrations(1, pagination.pageSize, value || null);
  };

  const handleTableChange = (nextPagination) => {
    loadRegistrations(nextPagination.current, nextPagination.pageSize, statusFilter);
  };

  const openCheckInScanner = () => {
    window.location.assign(`/event-checkin.html?eventId=${encodeURIComponent(eventId)}`);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 65, fixed: 'left' },
    {
      title: 'Người đăng ký',
      key: 'registrant',
      width: 220,
      fixed: 'left',
      render: (_, record) => (
        <div style={{ lineHeight: 1.65 }}>
          <div><strong>{record.TenNguoiDangKy || '—'}</strong></div>
          <div style={{ color: '#666', fontSize: 12 }}>{record.SDTNguoiDangKy || '—'}</div>
          {record.Gmail && <div style={{ color: '#888', fontSize: 12 }}>{record.Gmail}</div>}
          {record.TenDoanhNghiep && <div style={{ color: '#888', fontSize: 12 }}>{record.TenDoanhNghiep}</div>}
        </div>
      )
    },
    {
      title: 'Số lượng',
      key: 'quantity',
      width: 135,
      render: (_, record) => (
        <div>
          <div style={detailRowStyle}>
            <span style={detailLabelStyle}>Tham gia</span>
            <strong>{Number(record.SoNguoiThamGia ?? record.SoNguoiDiCung) || 0}</strong>
          </div>
          <div style={detailRowStyle}>
            <span style={detailLabelStyle}>Dự tiệc</span>
            <strong>{Number(record.SoNguoiDuTiec) || 0}</strong>
          </div>
        </div>
      )
    },
    {
      title: 'Chi phí',
      key: 'cost',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={detailRowStyle}>
            <span style={detailLabelStyle}>Sự kiện</span>
            <span>{record.TienSuKien == null ? '—' : formatMoney(record.TienSuKien)}</span>
          </div>
          <div style={detailRowStyle}>
            <span style={detailLabelStyle}>Dự tiệc</span>
            <span>{record.TienDuTiec == null ? '—' : formatMoney(record.TienDuTiec)}</span>
          </div>
        </div>
      )
    },
    {
      title: 'Công nợ',
      key: 'debt',
      width: 200,
      render: (_, record) => {
        const isLegacy = record.SoTienPhaiThu == null;
        const remaining = Number(record.SoTienConThieu || 0);

        if (isLegacy) return <Tag>Legacy</Tag>;

        return (
          <div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Phải thu</span>
              <strong>{formatMoney(record.SoTienPhaiThu)}</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Đã thu</span>
              <span style={{ color: Number(record.SoTienDaThanhToan || 0) > 0 ? '#389e0d' : undefined }}>
                {formatMoney(record.SoTienDaThanhToan)}
              </span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Còn thiếu</span>
              <strong style={{ color: remaining > 0 ? '#cf1322' : '#389e0d' }}>
                {formatMoney(remaining)}
              </strong>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Thanh toán',
      key: 'payment',
      width: 220,
      render: (_, record) => (
        <div style={{ lineHeight: 1.65 }}>
          <div style={{ marginBottom: 4 }}>
            {record.MaThanhToan ? <Text copyable>{record.MaThanhToan}</Text> : <Text type="secondary">Không có mã</Text>}
          </div>
          <div style={{ marginBottom: 4 }}>{paymentStatusTag(record.TrangThaiThanhToan)}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {record.PhuongThucThanhToan || 'Chưa xác định phương thức'}
          </div>
          {record.NgayThanhToan && (
            <div style={{ fontSize: 12, color: '#888' }}>
              {dayjs(record.NgayThanhToan).format('DD/MM/YYYY HH:mm')}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Duyệt đăng ký',
      key: 'approval',
      width: 230,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 8 }}>{approvalStatusTag(record.TrangThai)}</div>
          <Space size={[6, 6]} wrap>
            {record.TrangThai !== 'Đã Duyệt' && (
              <Button
                type="primary"
                size="small"
                disabled={!canApproveRegistration(record)}
                onClick={() => handleRegistrationStatusChange(record, 'Đã Duyệt')}
              >
                Duyệt
              </Button>
            )}
            {record.TrangThai !== 'Bị Từ Chối' && (
              <Button
                danger
                size="small"
                onClick={() => handleRegistrationStatusChange(record, 'Bị Từ Chối')}
              >
                Từ chối
              </Button>
            )}
            {record.TrangThai !== 'Chưa Duyệt' && (
              <Button
                size="small"
                onClick={() => handleRegistrationStatusChange(record, 'Chưa Duyệt')}
              >
                Về chưa duyệt
              </Button>
            )}
          </Space>
          {!canApproveRegistration(record) && record.TrangThai !== 'Đã Duyệt' && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#d48806' }}>
              Chưa đủ điều kiện thanh toán để duyệt
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 185,
      fixed: 'right',
      render: (_, record) => {
        const canConfirmCash = record.TrangThaiThanhToan === 'Chờ Thanh Toán' &&
          Number(record.SoTienDaThanhToan || 0) === 0 &&
          Number(record.SoTienPhaiThu || 0) > 0;

        return (
          <Space direction="vertical" size={6}>
            {canConfirmCash && (
              <Button type="primary" size="small" icon={<DollarOutlined />} onClick={() => openCashModal(record)}>
                Xác nhận tiền mặt
              </Button>
            )}
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa đăng ký này?"
              onConfirm={() => handleDeleteRegistration(record.id)}
              okText="Có"
              cancelText="Không"
            >
              <Button danger size="small" icon={<DeleteOutlined />}>Xóa</Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/events/list')}>Danh sách sự kiện</Button>
        <Button icon={<ReloadOutlined />} loading={loading || eventLoading} onClick={() => { loadEvent(); loadRegistrations(); }}>Làm mới</Button>
        <Button type="primary" icon={<QrcodeOutlined />} onClick={openCheckInScanner}>
          Quét QR Check-in
        </Button>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>Danh sách đăng ký sự kiện</Title>
        <Text type="secondary">{event?.TenSuKien || `Sự kiện #${eventId}`}</Text>

        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 4 }} style={{ marginTop: 16 }}>
          <Descriptions.Item label="Ngày tổ chức">{event?.Date ? dayjs(event.Date).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
          <Descriptions.Item label="Giá sự kiện">{event ? formatMoney(event.Gia) : '—'}</Descriptions.Item>
          <Descriptions.Item label="Dự tiệc">{Number(event?.ChoPhepDuTiec) === 1 ? 'Có' : 'Không'}</Descriptions.Item>
          <Descriptions.Item label="Giá dự tiệc">{Number(event?.ChoPhepDuTiec) === 1 ? formatMoney(event?.GiaDuTiec) : '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}><Card><Statistic title="Tổng đăng ký" value={pagination.total} /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card><Statistic title="Suất tham gia đã duyệt" value={totalAttendees} suffix="suất" /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card><Statistic title="Suất dự tiệc đã duyệt" value={totalDinner} suffix="suất" /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card><Statistic title="Tổng tiền đã thu" value={totalPaidAmount} formatter={(value) => formatMoney(value)} /></Card></Col>
      </Row>

      <Card>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Đăng ký thanh toán V2 chỉ được duyệt sau khi Đã Thanh Toán hoặc Không Cần Thanh Toán. Dữ liệu legacy vẫn giữ quy tắc cũ."
        />

        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Lọc nhanh tên, SĐT, email, mã SK trong trang hiện tại"
            style={{ width: 340 }}
          />
          <Select
            allowClear
            placeholder="Trạng thái duyệt"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            style={{ width: 180 }}
          >
            <Option value="Chưa Duyệt">Chưa Duyệt</Option>
            <Option value="Đã Duyệt">Đã Duyệt</Option>
            <Option value="Bị Từ Chối">Bị Từ Chối</Option>
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredRegistrations}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1450 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Tổng ${total} đăng ký`
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title={`Xác nhận tiền mặt${cashRecord?.MaThanhToan ? ` - ${cashRecord.MaThanhToan}` : ''}`}
        open={cashModalVisible}
        onOk={confirmCashPayment}
        onCancel={() => !cashSubmitting && setCashModalVisible(false)}
        okText="Xác nhận đã nhận tiền"
        cancelText="Hủy"
        confirmLoading={cashSubmitting}
        width={560}
      >
        {cashRecord && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Chỉ xác nhận khi bạn đã thực sự nhận đủ tiền mặt."
              description="Sau khi xác nhận, đăng ký chuyển sang Đã Thanh Toán nhưng vẫn cần thao tác Đã Duyệt riêng."
            />
            <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Người đăng ký">{cashRecord.TenNguoiDangKy || '—'}</Descriptions.Item>
              <Descriptions.Item label="Mã thanh toán">{cashRecord.MaThanhToan || '—'}</Descriptions.Item>
              <Descriptions.Item label="Phải thu"><strong style={{ color: '#cf1322' }}>{formatMoney(cashRecord.SoTienPhaiThu)}</strong></Descriptions.Item>
            </Descriptions>
            <div style={{ marginBottom: 12 }}>
              <label>Số tiền đã nhận</label>
              <InputNumber
                value={cashAmount}
                onChange={(value) => setCashAmount(Number(value) || 0)}
                min={0}
                style={{ width: '100%' }}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
              />
            </div>
            <div>
              <label>Ghi chú</label>
              <Input.TextArea
                value={cashNote}
                onChange={(e) => setCashNote(e.target.value)}
                maxLength={250}
                showCount
                rows={3}
                placeholder="Ví dụ: Thu tiền mặt tại sự kiện"
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default EventRegistrations;